import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { createSample, getBooksByType } from '../services/samples_service'
import './Samples.css'

const BOOK_TYPE_LABELS = {
  published: 'Published books',
  published_book: 'Published books',
  exercise: 'Exercise books',
  exercise_book: 'Exercise books',
}

const getBookId = (book) =>
  book?.bookid ?? book?.bookId ?? book?.book_id ?? book?.bookID ?? book?.id
const getBookName = (book) =>
  book?.name ??
  book?.bookname ??
  book?.bookName ??
  book?.book_title ??
  book?.book_name ??
  book?.title ??
  '-'
const getBookStock = (book) => {
  const stock = Number(book?.instock)
  return Number.isFinite(stock) ? Math.max(0, stock) : 0
}
const normalizeBooks = (books, type) =>
  (Array.isArray(books) ? books : [])
    .map((book) => ({
      ...book,
      id: getBookId(book),
      title: getBookName(book),
      instock: getBookStock(book),
      category: BOOK_TYPE_LABELS[type],
    }))
    .filter((book) => book.id != null && book.title !== '-')

function NewSample() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [category, setCategory] = useState('published_book')
  const [books, setBooks] = useState([])
  const [filter, setFilter] = useState('')
  const [selectedBooks, setSelectedBooks] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [isLoadingBooks, setIsLoadingBooks] = useState(true)
  const [error, setError] = useState('')
  const loginUserId = user?.userid ?? user?.user_id ?? user?.id

  useEffect(() => {
    let isCurrentRequest = true

    const loadBooks = async () => {
      setIsLoadingBooks(true)
      setError('')
      try {
        const result = await getBooksByType(category)
        if (isCurrentRequest) setBooks(normalizeBooks(result, category))
      } catch (requestError) {
        if (isCurrentRequest) {
          setBooks([])
          setError(requestError.message)
        }
      } finally {
        if (isCurrentRequest) setIsLoadingBooks(false)
      }
    }

    loadBooks()
    return () => {
      isCurrentRequest = false
    }
  }, [category])

  const visibleBooks = useMemo(() => {
    const search = filter.trim().toLowerCase()
    return books.filter((book) => book.title.toLowerCase().includes(search))
  }, [books, filter])

  const addBook = (book) => {
    setSelectedBooks((books) =>
      books.some((selectedBook) => selectedBook.bookid === book.id)
        ? books
        : [
            ...books,
            {
              bookid: book.id,
              name: book.title,
              quantity: Math.min(1, book.instock),
              instock: book.instock,
            },
          ],
    )
  }

  const updateQuantity = (bookid, quantity) => {
    const requestedQuantity = Number(quantity) || 1
    const selectedBook = selectedBooks.find((book) => book.bookid === bookid)
    const nextQuantity = Math.min(requestedQuantity, selectedBook?.instock ?? 0)

    if (selectedBook && requestedQuantity > selectedBook.instock) {
      setError(
        `${selectedBook.name} has only ${selectedBook.instock} in stock. Quantity was set to the available stock.`,
      )
    }

    setSelectedBooks((books) =>
      books.map((book) =>
        book.bookid === bookid
          ? { ...book, quantity: Math.max(1, nextQuantity) }
          : book,
      ),
    )
  }

  const removeBook = (bookid) => {
    setSelectedBooks((books) => books.filter((book) => book.bookid !== bookid))
  }

  const requestSample = async (event) => {
    event.preventDefault()

    if (!loginUserId) {
      setError('Unable to identify the logged-in user. Please sign in again.')
      return
    }

    setIsSaving(true)
    setError('')

    try {
      const hasInvalidQuantity = selectedBooks.some(
        (book) => book.quantity > book.instock,
      )
      if (hasInvalidQuantity) {
        setSelectedBooks((books) =>
          books.map((book) => ({
            ...book,
            quantity: Math.min(book.quantity, book.instock),
          })),
        )
        setError('A selected book exceeds its available stock.')
        return
      }

      const books = selectedBooks.map(({ bookid, name, quantity }) => ({
        bookid,
        name,
        quantity,
      }))
      const samplePayload = {
        books,
        userid: loginUserId,
      }

      await createSample(samplePayload)
      navigate('/samples')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className='samples-page'>
      <nav className='samples-page__breadcrumb' aria-label='Breadcrumb'>
        <button type='button' onClick={() => navigate('/samples')}>
          Samples
        </button>
        <span aria-hidden='true'>/</span>
        <strong>New request</strong>
      </nav>
      <header className='samples-page__header'>
        <div>
          <span className='app-content__eyebrow'>New request</span>
          <h1>Request sample</h1>
          <p>Submit a new sample request for distribution.</p>
        </div>
      </header>
      {error && (
        <div className='samples-page__error' role='alert'>
          {error}
        </div>
      )}
      <form className='samples-new-layout' onSubmit={requestSample}>
        <section className='samples-new-card samples-book-picker'>
          <div className='samples-new-card__heading'>
            <div>
              <span className='app-content__eyebrow'>Book catalogue</span>
              <h2>Choose books</h2>
            </div>
            <label className='samples-select-field'>
              <span>Book type</span>
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              >
                <option value='published_book'>Published books</option>
                <option value='exercise_book'>Exercise book one</option>
              </select>
            </label>
          </div>
          <label className='samples-filter-field'>
            <span>Filter books</span>
            <input
              type='search'
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder='Search by title'
            />
          </label>
          <div className='samples-book-list' aria-label='Available books'>
            {isLoadingBooks ? (
              <p className='samples-book-list__empty'>Loading books...</p>
            ) : visibleBooks.length ? (
              visibleBooks.map((book) => {
                const isSelected = selectedBooks.some(
                  (selectedBook) => selectedBook.bookid === book.id,
                )
                return (
                  <button
                    className={`samples-book-item${isSelected ? ' samples-book-item--selected' : ''}`}
                    key={book.id}
                    type='button'
                    onClick={() => addBook(book)}
                    disabled={isSelected || book.instock < 1}
                  >
                    <span>
                      <strong>{book.title}</strong>
                      <small>
                        {book.category} · {book.instock} in stock
                      </small>
                    </span>
                    <span className='samples-book-item__add' aria-hidden='true'>
                      +
                    </span>
                  </button>
                )
              })
            ) : (
              <p className='samples-book-list__empty'>
                No books match this filter.
              </p>
            )}
          </div>
        </section>

        <section className='samples-new-card samples-selected-books'>
          <div className='samples-new-card__heading'>
            <div>
              <span className='app-content__eyebrow'>Your selection</span>
              <h2>Selected books</h2>
            </div>
            <span className='samples-selected-books__count'>
              {selectedBooks.length} selected
            </span>
          </div>
          {selectedBooks.length ? (
            <div className='samples-selected-table-wrap'>
              <table className='samples-selected-table'>
                <thead>
                  <tr>
                    <th>Book</th>
                    <th>Quantity</th>
                    <th>
                      <span className='sr-only'>Remove</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedBooks.map((book) => (
                    <tr key={book.bookid}>
                      <td>
                        <strong>{book.name}</strong>
                      </td>
                      <td>
                        <input
                          aria-label={`Quantity for ${book.name}`}
                          type='number'
                          min='1'
                          max={book.instock}
                          value={book.quantity}
                          onChange={(event) =>
                            updateQuantity(book.bookid, event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <button
                          className='samples-selected-table__remove'
                          type='button'
                          onClick={() => removeBook(book.bookid)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='samples-selected-books__empty'>
              <strong>No books selected</strong>
              <span>Choose a book from the catalogue to add it here.</span>
            </div>
          )}
        </section>

        <div className='samples-new-actions'>
          <button type='button' onClick={() => navigate('/samples')}>
            Cancel
          </button>
          <button type='submit' disabled={isSaving || !selectedBooks.length}>
            {isSaving ? 'Submitting...' : 'Submit request'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default NewSample
